import React, { useState } from "react";

type Order = "asc" | "desc";

interface UseMuiTableProps {
    defaultOrder?: Order;
    defaultOrderBy?: string;
    defaultPage?: number;
    defaultRowsPerPage?: number;
}

function descendingComparator<T extends Record<string, number | string>>(a: T, b: T, orderBy: string): number {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}
export function getComparator<T extends Record<string, number | string>>(
    order: Order,
    orderBy: string
): (a: T, b: T) => number {
    return order === `asc`
        ? (a: T, b: T) => descendingComparator(a, b, orderBy)
        : (a: T, b: T) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
export function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
    const stabilizedThis: [T, number][] = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

// ==============================================================

// ==============================================================

const useMuiTable = (props: UseMuiTableProps) => {
    const [order, setOrder] = useState<Order>(props.defaultOrder || `asc`);
    const [orderBy, setOrderBy] = useState<string>(props.defaultOrderBy || `date`);
    const [page, setPage] = useState<number>(props.defaultPage || 0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(props.defaultRowsPerPage || 25);
    const handleRequestSort = (_: React.MouseEvent, property: string): void => {
        const isAsc = orderBy === property && order === `asc`;
        setOrder(isAsc ? `desc` : `asc`);
        setOrderBy(property);
    };
    const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => setPage(newPage);
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const emptyRows = (pg: number, rpp: number, dataLength: number): number =>
        pg > 0 ? Math.max(0, (1 + page) * rpp - dataLength) : 0;
    return {
        page,
        order,
        setPage,
        orderBy,
        rowsPerPage,
        emptyRows,
        handleChangePage,
        handleRequestSort,
        handleChangeRowsPerPage,
    };
};
export default useMuiTable;
